import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useFetch, Card, Loader, PageHeader, Button } from "../ui";
import { Chip } from "./CommandCenter";

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const Row = ({ k, v }) => <p className="mt-1.5 text-sm"><span className="font-bold text-[#8A939B]">{k}:</span> <span className="text-white">{v || "—"}</span></p>;

export default function UniversalRecord() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, reload } = useFetch(`/admin/delivery/record/${id}`, [id]);
  if (loading || !data) return <Loader />;
  const { client, acquisition: acq, project, tasks, assets, audit_trail: trail, lifecycle_stages: stages } = data;
  const chip = client.at_risk ? "AT RISK" : client.client_blocked ? "WAITING ON CLIENT" : client.founder_approval_required ? "FOUNDER APPROVAL" : client.hl_blocked ? "WAITING ON HUGHLAUNCH" : "ON TRACK";

  const setStage = async (stage) => {
    try { await api.post(`/admin/delivery/clients/${id}/stage`, { stage }); toast.success(`Stage → ${stage}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const toggleFlag = async (key, val) => {
    try { await api.post(`/admin/delivery/clients/${id}/flags`, { [key]: val }); reload(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Universal Client Record" title={client.company_name}
        subtitle={`${client.contact_name || ""} · ${client.package || ""}`}
        actions={<div className="flex flex-wrap items-center gap-2">
          <Chip label={chip} />
          <Button variant="ghost" onClick={() => navigate(`/admin/delivery/${id}`)} data-testid="open-delivery-workspace">Delivery Workspace</Button>
          <Button variant="ghost" onClick={() => navigate(`/admin/clients/${id}`)} data-testid="open-classic-client">Open Client Workspace</Button>
        </div>} />

      <Card className="p-5" data-testid="ur-lifecycle">
        <p className="mb-3 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Lifecycle Stage</p>
        <div className="flex flex-wrap gap-1.5">
          {stages.map((s) => (
            <button key={s} data-testid={`ur-stage-${s.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => setStage(s)}
              className={`rounded-full px-3 py-1.5 text-[11px] font-extrabold ${(client.lifecycle_stage || "ONBOARDING") === s ? "bg-[#C6FF00] text-[#0A0C0F]" : "bg-[#14161b] text-[#8A939B] hover:text-white"}`}>{s}</button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {[["client_blocked", "Client Blocked"], ["hl_blocked", "HL Blocked"], ["at_risk", "At Risk"], ["founder_approval_required", "Founder Approval"]].map(([k, label]) => (
            <button key={k} data-testid={`ur-flag-${k}`} onClick={() => toggleFlag(k, !client[k])}
              className={`rounded-full border px-3 py-1.5 text-xs font-bold ${client[k] ? "border-red-500/50 bg-red-500/10 text-red-400" : "border-[#23262c] text-[#8A939B] hover:border-[#3a3f47]"}`}>{label}: {client[k] ? "ON" : "OFF"}</button>
          ))}
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card className="p-5" data-testid="ur-contact">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Contact</p>
          <Row k="Contact" v={client.contact_name} /><Row k="Email" v={client.email || client.contact_email} />
          <Row k="Phone" v={client.phone} /><Row k="Website" v={client.website} />
          <Row k="Industry" v={client.industry || acq?.industry} /><Row k="Source" v={acq?.lead_source || client.referral_source} />
        </Card>
        <Card className="p-5" data-testid="ur-sales">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Sales</p>
          <Row k="Brand Score" v={acq ? `${(acq.final_scores || acq.preliminary_scores)?.overall}/100` : "No audit"} />
          <Row k="Opportunity" v={acq ? `${acq.opportunity_score} · ${acq.opportunity_class}` : "—"} />
          <Row k="Prescription" v={acq?.prescription_sent_at ? "Sent" : "—"} />
          <Row k="Package" v={client.package} />
          <Row k="Contract Value" v={money(client.project?.price || client.contract_value)} />
          <Row k="Outstanding" v={money(client.outstanding)} />
          {acq && <Button variant="ghost" className="mt-3" onClick={() => navigate(`/admin/acquisition/${acq.id}`)} data-testid="ur-open-acq">Open Acquisition CRM</Button>}
        </Card>
        <Card className="p-5" data-testid="ur-project">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Project</p>
          <Row k="Name" v={project?.name || project?.project_name} />
          <Row k="Progress" v={`${project?.progress || 0}%`} />
          <Row k="Stage" v={client.lifecycle_stage || "ONBOARDING"} />
          <Row k="Start" v={project?.start_date} /><Row k="Est. Launch" v={project?.estimated_completion} />
          <Row k="Manager" v={client.assigned_manager} />
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-[#14161b]"><div className="h-full bg-[#C6FF00]" style={{ width: `${project?.progress || 0}%` }} /></div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="p-5" data-testid="ur-tasks">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Tasks ({tasks.length})</p>
          {tasks.length === 0 && <p className="text-sm text-[#6B7280]">No tasks.</p>}
          {tasks.slice(0, 10).map((t) => (
            <div key={t.id} className="mt-2 flex items-center justify-between rounded-xl bg-[#14161b] px-3 py-2.5 text-sm">
              <span className="font-semibold text-white">{t.title}</span>
              <span className="flex items-center gap-2">
                <span className="rounded-full bg-[#1c1f25] px-2 py-0.5 text-[10px] font-extrabold text-[#C6FF00]">{t.owner_type || "OPERATIONS"}</span>
                <span className="text-xs text-[#8A939B]">{t.status}</span>
              </span>
            </div>
          ))}
        </Card>
        <Card className="p-5" data-testid="ur-assets">
          <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Assets ({assets.length})</p>
          {assets.length === 0 && <p className="text-sm text-[#6B7280]">No assets uploaded yet.</p>}
          {assets.slice(0, 10).map((a) => (
            <p key={a.id} className="mt-1.5 truncate text-sm text-[#c8cdd3]">{a.name || a.filename} <span className="text-xs text-[#6B7280]">· {a.category || a.type || "file"}</span></p>
          ))}
        </Card>
      </div>

      <Card className="p-5" data-testid="ur-audit-trail">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-[#8A939B]">Audit History</p>
        {trail.length === 0 && <p className="text-sm text-[#6B7280]">No recorded changes yet.</p>}
        {trail.map((e) => (
          <p key={e.id} className="mt-1.5 text-sm text-[#c8cdd3]">
            <span className="text-[#8A939B]">{new Date(e.at).toLocaleString()}</span> — <span className="font-bold text-white">{e.field}</span>: {String(e.old_value ?? "—")} → <span className="text-[#C6FF00]">{String(e.new_value)}</span> <span className="text-xs text-[#6B7280]">({e.source}{e.who ? ` · ${e.who}` : ""})</span>
          </p>
        ))}
      </Card>
    </div>
  );
}
